import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StorageIcon from '@mui/icons-material/Storage';
import StoreIcon from '@mui/icons-material/Store';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface StepStatus {
  completed: boolean;
  label: string;
}

interface DePINHubTutorialLauncherProps {
  onStartTutorial?: () => void;
  onDismiss: () => void;
  currentStep?: number;
  totalSteps?: number;
  isVisible?: boolean;
  stepsStatus?: StepStatus[];
}

const defaultSteps: StepStatus[] = [
  { label: 'Connect your wallet', completed: false },
  { label: 'Register a bucket', completed: false },
  { label: 'Browse the marketplace', completed: false },
  { label: 'Create your first deal', completed: false },
];

// Step descriptions and icons
const stepDetails = [
  {
    icon: AccountBalanceWalletIcon,
    title: 'Connect Your Wallet',
    description: 'Connect your Stellar or SUI wallet to start using the ISP Market.',
    action: 'Connect Wallet',
    color: '#617BFF',
  },
  {
    icon: StorageIcon,
    title: 'Register a Bucket',
    description: 'Add your first storage bucket to offer storage capacity on the network.',
    action: 'Add Bucket',
    color: '#10B981',
  },
  {
    icon: StoreIcon,
    title: 'Browse the Marketplace',
    description: 'Explore available storage offers from other providers.',
    action: 'View Marketplace',
    color: '#F59E0B',
  },
  {
    icon: HandshakeIcon,
    title: 'Create Your First Deal',
    description: 'Purchase storage capacity and create your first deal.',
    action: 'Create Deal',
    color: '#8B5CF6',
  },
];

const DePINHubTutorialLauncher: React.FC<DePINHubTutorialLauncherProps> = ({
  onStartTutorial,
  onDismiss,
  totalSteps = 4,
  isVisible = true,
  stepsStatus = defaultSteps,
}) => {
  const [isMinimized, setIsMinimized] = useState(true);
  const [showPulse, setShowPulse] = useState(true);

  // Stop pulse animation after first interaction
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  if (!isVisible) return null;

  const completedSteps = stepsStatus.filter(s => s.completed).length;
  const allCompleted = completedSteps === totalSteps;
  
  // Get the next step to complete
  const nextStepIndex = stepsStatus.findIndex(s => !s.completed);
  const nextStep = nextStepIndex >= 0 ? stepDetails[nextStepIndex] : null;
  const NextStepIcon = nextStep?.icon || PlayArrowIcon;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '80px',
        right: '24px',
        zIndex: 1000,
        animation: showPulse ? 'pulse 2s infinite' : 'none',
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.02)' },
          '100%': { transform: 'scale(1)' },
        },
      }}
    >
      {isMinimized ? (
        // Minimized state - just an icon button
        <Box
          onClick={() => setIsMinimized(false)}
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(97, 123, 255, 0.4)',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 6px 24px rgba(97, 123, 255, 0.5)',
            },
          }}
        >
          <SchoolIcon sx={{ fontSize: 28, color: 'white' }} />
          {completedSteps > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: '#10B981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
              }}
            >
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'white' }}>
                {completedSteps}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Expanded state - full card
        <Box
          sx={{
            width: 320,
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
          }}
        >
          {/* Header with gradient */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SchoolIcon sx={{ fontSize: 24, color: 'white' }} />
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'white' }}>
                ISP Market Guide
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(true)}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <Box sx={{ width: 14, height: 2, bgcolor: 'currentColor', borderRadius: 1 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={onDismiss}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            {/* Progress bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>Progress</Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#617BFF' }}>
                  {completedSteps}/{totalSteps} completed
                </Typography>
              </Box>
              <Box sx={{ height: 6, bgcolor: '#E5E7EB', borderRadius: 3 }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${(completedSteps / totalSteps) * 100}%`,
                    background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
                    borderRadius: 3,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            </Box>

            {/* Current Step Highlight */}
            {nextStep && !allCompleted && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '12px',
                  bgcolor: `${nextStep.color}10`,
                  border: `1px solid ${nextStep.color}30`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      bgcolor: nextStep.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <NextStepIcon sx={{ fontSize: 18, color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 500 }}>
                      Step {nextStepIndex + 1} of {totalSteps}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                      {nextStep.title}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#535862', lineHeight: 1.5, mb: 1.5 }}>
                  {nextStep.description}
                </Typography>
                {onStartTutorial && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PlayArrowIcon />}
                    onClick={onStartTutorial}
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      bgcolor: nextStep.color,
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      py: 0.75,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: nextStep.color, opacity: 0.9, boxShadow: 'none' },
                    }}
                  >
                    Start Guide
                  </Button>
                )}
              </Box>
            )}

            {/* All completed message */}
            {allCompleted && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '12px',
                  bgcolor: '#10B98110',
                  border: '1px solid #10B98130',
                  textAlign: 'center',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 32, color: '#10B981', mb: 1 }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 0.5 }}>
                  All steps completed!
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                  You're all set to use ISP Market.
                </Typography>
              </Box>
            )}

            {/* Steps list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {stepsStatus.map((step, idx) => {
                const stepDetail = stepDetails[idx];
                const StepIcon = stepDetail?.icon || PlayArrowIcon;
                const isCurrentStep = idx === nextStepIndex;
                return (
                  <Box 
                    key={idx} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 1,
                      borderRadius: '8px',
                      bgcolor: isCurrentStep ? '#F9FAFB' : 'transparent',
                    }}
                  >
                    {step.completed ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#10B981' }} />
                    ) : (
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: isCurrentStep ? stepDetail?.color : '#E5E7EB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <StepIcon sx={{ fontSize: 10, color: isCurrentStep ? 'white' : '#9CA3AF' }} />
                      </Box>
                    )}
                    <Typography 
                      sx={{ 
                        fontSize: '12px', 
                        color: step.completed ? '#10B981' : isCurrentStep ? '#101828' : '#667085',
                        fontWeight: step.completed || isCurrentStep ? 600 : 400,
                        textDecoration: step.completed ? 'line-through' : 'none',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DePINHubTutorialLauncher;
